/**
 * Task list bucketing for the Tasks page.
 *
 * Splits a flat task list into the three groups the Tasks page renders:
 *
 *   - `withDeadline` — active tasks that have a deadline, soonest first
 *   - `noDeadline`   — active tasks with no deadline (the backlog), newest first
 *   - `done`         — completed / cancelled tasks, most recently finished first
 *
 * "Active" means `status` is `open` or `in_progress`. The deadline split reuses
 * `splitDeadline` so `due_date` (date-only) and `deadline_at` (with time) are
 * treated uniformly, matching the rest of the app.
 */

import type { Task } from '@/types/tasks'
import { splitDeadline } from './taskDeadline'

export interface TaskBuckets {
  withDeadline: Task[]
  noDeadline: Task[]
  done: Task[]
}

/** Sortable key for a deadline: `YYYY-MM-DDTHH:MM`. No-time deadlines sort last within their day. */
function deadlineKey(task: Task): string {
  const { date, time } = splitDeadline(task)
  return `${date}T${time || '99:99'}`
}

/** Most-recent-first comparator on an ISO timestamp field (newest first). */
function byNewest(a: string | null, b: string | null): number {
  return (b ?? '').localeCompare(a ?? '')
}

/**
 * Group tasks into deadline / backlog / done buckets, each pre-sorted.
 *
 * @param tasks - flat list (e.g. from the data store, newest-created first)
 */
export function bucketTasks(tasks: Task[]): TaskBuckets {
  const withDeadline: Task[] = []
  const noDeadline: Task[] = []
  const done: Task[] = []

  for (const t of tasks) {
    if (t.status === 'done' || t.status === 'cancelled') {
      done.push(t)
      continue
    }
    const { date } = splitDeadline(t)
    if (date) withDeadline.push(t)
    else noDeadline.push(t)
  }

  withDeadline.sort((a, b) => deadlineKey(a).localeCompare(deadlineKey(b)))
  noDeadline.sort((a, b) => byNewest(a.created_at, b.created_at))
  done.sort((a, b) => byNewest(a.completed_at ?? a.created_at, b.completed_at ?? b.created_at))

  return { withDeadline, noDeadline, done }
}
